/**
 * Retention, as a compliance officer reads it.
 *
 * **`overdueRemaining > 0` is a breach, not a metric.** It is data we said we
 * would have deleted and have not. So it is not a number in a table — it is a
 * red banner naming the institutions whose data it is, because the first
 * question about a breach is whose.
 */

export interface OverdueInstitution {
  institutionId: string;
  name: string;
  operationalDays: number;
  overdue: number;
}

export interface RetentionRun {
  id: string;
  startedAt: string;
  finishedAt?: string;
  overdueRemaining: number;
  perInstitution: Array<{
    institutionId: string;
    sessionsDeleted: number;
    eventsDeleted: number;
    runsDeleted: number;
    participantsDeleted: number;
    scoresDeleted: number;
    notesDeleted: number;
    enrolmentsDeleted: number;
    overdueRemaining: number;
  }>;
  failures: Array<{ institutionId: string; stage: string; errorKey: string }>;
  incomplete?: boolean;
}

export interface RetentionStatus {
  lastRun: RetentionRun | null;
  missed: { healthy: boolean; lastRunAt?: string; hoursSince?: number };
  overdue: OverdueInstitution[];
  overdueTotal: number;
  maxAgeHours: number;
}

/**
 * Three things can be wrong, and they are not the same thing.
 *
 * `overdue` — data past its window is still here.
 * `missed`  — the job has not reported, which is indistinguishable from a job
 *             that had nothing to do unless absence is monitored.
 * `failed`  — the last run reported failures it could not get past.
 */
export type RetentionAlarm = "overdue" | "missed" | "failed";

export function alarmsIn(status: RetentionStatus | undefined): RetentionAlarm[] {
  if (!status) return [];

  const alarms: RetentionAlarm[] = [];
  if (status.overdueTotal > 0) alarms.push("overdue");
  if (!status.missed.healthy) alarms.push("missed");
  if ((status.lastRun?.failures.length ?? 0) > 0) alarms.push("failed");
  return alarms;
}

export const ALARM_KEY: Record<RetentionAlarm, string> = {
  overdue: "retention.alarm.overdue",
  missed: "retention.alarm.missed",
  failed: "retention.alarm.failed",
};

/** Everything a run removed, added up. Shown so a run reads as work done. */
export function deletedTotals(run: RetentionRun | null): {
  sessions: number;
  events: number;
  enrolments: number;
  other: number;
} {
  const rows = run?.perInstitution ?? [];

  return {
    sessions: rows.reduce((sum, row) => sum + row.sessionsDeleted, 0),
    events: rows.reduce((sum, row) => sum + row.eventsDeleted, 0),
    enrolments: rows.reduce((sum, row) => sum + row.enrolmentsDeleted, 0),
    other: rows.reduce(
      (sum, row) =>
        sum + row.runsDeleted + row.participantsDeleted + row.scoresDeleted + row.notesDeleted,
      0
    ),
  };
}

/** How long the last run took, in seconds, or nothing if it never finished. */
export function runDurationSeconds(run: RetentionRun | null): number | undefined {
  if (!run?.finishedAt) return undefined;

  const ms = Date.parse(run.finishedAt) - Date.parse(run.startedAt);
  return Number.isFinite(ms) ? Math.round(ms / 100) / 10 : undefined;
}

/**
 * The reason a manual run demands.
 *
 * The same twenty characters the backend requires. Checked here so the button
 * is honest about it rather than letting somebody type "dsr" and be refused by
 * a server they cannot see.
 */
export const MIN_REASON = 20;

export function reasonIsSufficient(reason: string): boolean {
  return reason.trim().length >= MIN_REASON;
}
